'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { DbChartECharts } from './db-chart-echarts.js';
/**
 * Sort the STORED display rows client-side (COP-114 §7 — never a re-query):
 * numeric comparison for `type === 'number'` columns, lexicographic
 * otherwise. Pure + exported so the sort contract is testable without DOM
 * events (node-env `renderToStaticMarkup` regime).
 */
export function sortRows(rows, column, dir) {
    const compare = column.type === 'number'
        ? (a, b) => Number(a) - Number(b)
        : (a, b) => String(a ?? '').localeCompare(String(b ?? ''));
    return [...rows].sort((ra, rb) => {
        const result = compare(ra[column.key], rb[column.key]);
        return dir === 'asc' ? result : -result;
    });
}
/**
 * Format one cell from the column's semantic-layer type metadata:
 * `number` → `Intl.NumberFormat`, `date` → locale date string, `datetime` →
 * locale date+time string, anything else → raw string. Pure + exported for
 * the same reason as `sortRows`.
 */
export function formatCell(value, column) {
    if (value === null || value === undefined)
        return '';
    if (column.type === 'number') {
        const n = Number(value);
        return Number.isFinite(n) ? new Intl.NumberFormat().format(n) : String(value);
    }
    if (column.type === 'date' || column.type === 'datetime') {
        const d = new Date(String(value));
        if (Number.isNaN(d.getTime()))
            return String(value);
        return column.type === 'date' ? d.toLocaleDateString() : d.toLocaleString();
    }
    return String(value);
}
const CHART_TYPE_LABELS = {
    bar: 'Bar',
    pareto: 'Pareto',
    groupedBar: 'Grouped bar',
    line: 'Line',
};
/**
 * Inline result card for a `data-db-table` part (M24; COP-114 §7,
 * DBC-07/DBC-08). Everything rendered comes from the part itself — the
 * client never loads semantic-layer, never re-queries, and shows a chart
 * if and only if the server-computed `data.chart` says one is available
 * (default type = first entry of `chart.available`).
 *
 * Column headers toggle an asc/desc client-side sort over the stored rows
 * (`sortRows`); cells format from column type metadata (`formatCell`).
 *
 * Unstyled. Consumers target `.copilot-db-table-card`,
 * `.copilot-db-table-card-{header,title,connector,executed,chart,chart-switch,
 * table,sort,footer,sampled}` and the `data-part-id` / `data-chart-type`
 * attributes.
 */
export function DbTableCard({ data }) {
    const [sort, setSort] = useState(null);
    const [chartType, setChartType] = useState(data.chart !== null && data.chart.available.length > 0
        ? data.chart.available[0]
        : null);
    const sortedRows = useMemo(() => {
        if (sort === null)
            return data.rows;
        const column = data.columns.find((c) => c.key === sort.key);
        if (!column)
            return data.rows;
        return sortRows(data.rows, column, sort.dir);
    }, [data.rows, data.columns, sort]);
    const onHeaderClick = (key) => {
        setSort((prev) => prev?.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'asc' });
    };
    return (_jsxs("div", { className: "copilot-db-table-card", "data-part-id": data.id, "data-connector-id": data.connectorId, role: "region", "aria-label": "Query result", children: [_jsxs("div", { className: "copilot-db-table-card-header", children: [_jsx("span", { className: "copilot-db-table-card-title", children: data.title }), _jsx("span", { className: "copilot-db-table-card-connector", "data-connector-badge": "", children: data.connectorName }), _jsx("span", { className: "copilot-db-table-card-executed", children: new Date(data.executedAt).toLocaleString() })] }), data.chart !== null && chartType !== null && (_jsxs("div", { className: "copilot-db-table-card-chart", children: [_jsx("div", { className: "copilot-db-table-card-chart-switcher", role: "group", "aria-label": "Chart type", children: data.chart.available.map((type) => (_jsx("button", { type: "button", className: "copilot-db-table-card-chart-switch", "data-chart-type": type, "data-active": type === chartType, "aria-pressed": type === chartType, onClick: () => setChartType(type), children: CHART_TYPE_LABELS[type] }, type))) }), _jsx(DbChartECharts, { type: chartType, columns: data.columns, rows: data.rows })] })), _jsx("div", { className: "copilot-db-table-card-table", children: _jsxs("table", { children: [_jsx("thead", { children: _jsx("tr", { children: data.columns.map((column) => (_jsx("th", { "aria-sort": sort?.key === column.key
                                        ? sort.dir === 'asc'
                                            ? 'ascending'
                                            : 'descending'
                                        : undefined, children: _jsxs("button", { type: "button", className: "copilot-db-table-card-sort", onClick: () => onHeaderClick(column.key), children: [column.path, sort?.key === column.key && (_jsx("span", { "aria-hidden": "true", children: sort.dir === 'asc' ? ' ▲' : ' ▼' }))] }) }, column.key))) }) }), _jsx("tbody", { children: sortedRows.map((row, ri) => (_jsx("tr", { children: data.columns.map((column) => (_jsx("td", { children: formatCell(row[column.key], column) }, column.key))) }, ri))) })] }) }), data.truncated && (_jsxs("div", { className: "copilot-db-table-card-footer", children: ["showing ", data.rows.length, " of ", data.totalRowCount, data.rows.length < data.totalRowCount && (_jsx("span", { className: "copilot-db-table-card-sampled", children: " (sampled)" }))] }))] }));
}
//# sourceMappingURL=db-table-card.js.map